import type { RelationOutputForSeller } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { useUpdateRelation } from "@/services";
import { currentUserAtom } from "@/store/login";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import React from "react";
import { RelationForm } from "./RelationsForm";
import type { RelationFormData } from "./relationsForm.schema";

interface RelationsFormEditProps {
	selectedRelation: RelationOutputForSeller;
	open: boolean;
	onClose: () => void;
}

const RelationsFormEdit: React.FC<RelationsFormEditProps> = ({
	selectedRelation,
	open,
	onClose,
}) => {
	const { t } = useTranslation();
	const updateRelation = useUpdateRelation();
	const user = useAtomValue(currentUserAtom);

	const isAllowToEdit =
		// selectedRelation.requester === "SELLER" &&
		selectedRelation.seller_id === user?.id;

	const initialValues = React.useMemo(
		() => ({
			maxDebt:
				selectedRelation.max_debt === null
					? null
					: Number(selectedRelation.max_debt),
			localPrice:
				selectedRelation.local_price === null
					? null
					: +selectedRelation.local_price,
			useInheritedPrice: selectedRelation.local_price === null,
			isAllowToOrder: selectedRelation.local_is_allowed_to_order,
		}),
		[selectedRelation],
	);

	const handleSubmit = async (data: RelationFormData) => {
		await updateRelation.mutateAsync({
			path: { relation_id: selectedRelation.id },
			body: {
				max_debt: data.maxDebt,
				local_price: data.localPrice,
				is_allowed_to_order: data.isAllowToOrder,
			},
		});
		onClose();
	};

	if (!isAllowToEdit) return null;

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="w-full max-w-120">
				<DialogHeader>
					<DialogTitle>{t("relations.edit.dialogTitle")}</DialogTitle>
				</DialogHeader>
				<DialogScrollableArea className="max-h-[90vh] pt-0 pb-6">
					<RelationForm
						initialValues={initialValues}
						loading={updateRelation.isPending}
						onSubmit={handleSubmit}
					/>
				</DialogScrollableArea>
			</DialogContent>
		</Dialog>
	);
};

export default RelationsFormEdit;
